// Lightweight duration formatter — milliseconds → human-readable string.
// Shared by AlertHistory TableView, AlertReport, and the notification bell.
//
// Examples:
//   formatDurationMs(450)        →  "0s"  (rounded down)
//   formatDurationMs(8200)       →  "8s"
//   formatDurationMs(72000)      →  "1m 12s"
//   formatDurationMs(7320000)    →  "2h 2m"
//   formatDurationMs(86400000)   →  "1d 0h"
//
// For open intervals (no ended_at yet) callers compute elapsed = Date.now() -
// new Date(started_at).getTime() and pass it here.

export function formatDurationMs(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return '—';
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const totalMin = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  if (totalMin < 60) {
    return secs > 0 ? `${totalMin}m ${secs}s` : `${totalMin}m`;
  }
  const totalHours = Math.floor(totalMin / 60);
  const mins = totalMin % 60;
  if (totalHours < 24) {
    return mins > 0 ? `${totalHours}h ${mins}m` : `${totalHours}h`;
  }
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return `${days}d ${hours}h`;
}

/** True when an alert row represents a currently-open interval. */
export function isOpenInterval(row) {
  return row?.shape === 'interval' && row?.ended_at == null;
}

/**
 * Milliseconds since this open interval started, or null if the row
 * isn't an open interval (or the timestamp is malformed).
 */
export function openIntervalElapsedMs(row, nowMs = Date.now()) {
  if (!isOpenInterval(row)) return null;
  const startedAt = row.started_at ?? row.triggered_at;
  if (!startedAt) return null;
  const startMs = new Date(startedAt).getTime();
  if (!Number.isFinite(startMs)) return null;
  return Math.max(0, nowMs - startMs);
}
